import { useCallback, useMemo } from "preact/hooks";
import PropTypes from "prop-types";
import CountUp from "react-countup";

GCXTotalsTable.propTypes = {
  days: PropTypes.arrayOf(PropTypes.string).isRequired,
  timeBlockLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  items: PropTypes.array.isRequired,
  currentAmount: PropTypes.number,
  currentStreamerName: PropTypes.string,
};
export default function GCXTotalsTable({
  days,
  timeBlockLabels,
  items,
  currentAmount = 0,
  currentStreamerName = "",
}) {
  const now = new Date();

  const nowPieces = {
    day: now.getDate(),
    month: now.getMonth() + 1,
  };

  const getDayPieces = useCallback((day) => {
    const [month, dayNum] = day.split(" ");

    let monthNum = 0;

    if (month === "July") {
      monthNum = 7;
    }

    return {
      day: parseInt(dayNum.replace(/\D/g, ""), 10),
      month: monthNum,
    };
  }, []);

  const highlight = useCallback(
    (rawDay) => {
      const { day, month } = getDayPieces(rawDay);
      return day === nowPieces.day && month === nowPieces.month;
    },
    [getDayPieces, nowPieces.day, nowPieces.month],
  );

  const getRowContent = useCallback(
    (timeBlockLabel, index) => {
      return days.map((day) => {
        const item = items.find(
          (item) => item.day === day && item.timeBlockLabel === timeBlockLabel,
        );

        let amountToDisplay = 0;

        const shouldHighlight = highlight(day);

        if (item && item.streamerName !== "Coming Soon") {
          if (currentAmount > 0 && item.endAmount === 0 && item.startAmount > 0) {
            amountToDisplay = currentAmount - item.startAmount;
          } else if (item.endAmount === 0 && item.startAmount === 0 && currentAmount === 0) {
            amountToDisplay = 0;
          } else {
            let amount = parseFloat(item.endAmount) - parseFloat(item.startAmount);

            if (amount < 0) {
              amount = 0;
            }

            amountToDisplay = amount;
          }

          const splitName = item.streamerName.includes("&")
            ? item.streamerName.split("&")
            : item.streamerName.split("/");

          return (
            <td
              key={`${day}-${timeBlockLabel}`}
              className={
                shouldHighlight && currentStreamerName.includes(item.streamerName)
                  ? "text-xs bg-primary/20"
                  : "text-xs"
              }
            >
              {splitName.map((name) => {
                if (name === currentStreamerName && shouldHighlight) {
                  return (
                    <div key={name} className={`badge badge-sm badge-primary mr-1`}>
                      {name}
                    </div>
                  );
                }

                return (
                  <div
                    key={name}
                    className={`badge text-white badge-sm ${index % 2 === 0 ? "badge-secondary" : "badge-success"} mr-1`}
                  >
                    {name}
                  </div>
                );
              })}

              <span className="text-md font-bold">
                <CountUp
                  end={Math.max(0, amountToDisplay)}
                  prefix="$"
                  preserveValue
                  decimalPlaces={2}
                />
              </span>
            </td>
          );
        }

        return (
          <td className="text-xs text-gray-500" key={`${day}-${timeBlockLabel}`}>
            N/A
          </td>
        );
      });
    },
    [days, items, currentAmount, currentStreamerName, highlight],
  );

  const parseTimeToMinutes = useCallback((timeStr) => {
    const match = timeStr.match(/^(\d+)(AM|PM)$/i);
    if (!match) {
      return 0;
    }
    const hourStr = match[1];
    const period = match[2];
    let hour = parseInt(hourStr, 10);
    if (period.toUpperCase() === "AM") {
      if (hour === 12) {
        hour = 0;
      }
    } else if (period.toUpperCase() === "PM") {
      if (hour !== 12) {
        hour += 12;
      }
    }
    return hour * 60;
  }, []);

  const sortedTimeBlockLabels = useMemo(() => {
    return timeBlockLabels.sort((a, b) => {
      const aStart = a.split(" - ")[0].replace(/\s/g, "");
      const bStart = b.split(" - ")[0].replace(/\s/g, "");
      return parseTimeToMinutes(aStart) - parseTimeToMinutes(bStart);
    });
  }, [timeBlockLabels, parseTimeToMinutes]);

  return (
    <table className="table table-zebra table-pin-rows table-pin-cols">
      <thead>
        <tr className="select-none">
          <th className="min-w-[120px] select-none">Time Block</th>
          {days
            .sort((a, b) => parseInt(a.replace(/\D/g, ""), 10) - parseInt(b.replace(/\D/g, ""), 10))
            .map((day) => (
              <td
                className={`${highlight(day) ? "bg-base-200 text-primary min-w-[350px]" : "min-w-[350px]"}`}
                key={`day-head-${day}}`}
              >
                {day}
              </td>
            ))}
          <th className="min-w-[120px]">Time Block</th>
        </tr>
      </thead>
      <tbody>
        {sortedTimeBlockLabels.map((label, index) => (
          <tr key={`time-block-${label}`}>
            <th className="select-none">{label}</th>
            {getRowContent(label, index)}
            <th className="select-none">{label}</th>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
